import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Table, Select, Spin, Empty, Card, Button } from "antd";
import { fetchPostReports } from "../../services/reports";
import UpgradeModal from "../BuySubscription/UpgradeModal";

const { Option } = Select;

const SUPPORTED_REPORT_PLATFORMS = [
  "FACEBOOK",
  "INSTAGRAM",
  "PINTEREST",
  "LINKEDIN_COMPANY",
  "YOUTUBE",
];

const REPORTS_UPGRADE_MESSAGE =
  "Reports are not included in your current plan. Please upgrade to access Profile and Post Reports.";

const getPlatformLabel = (platform) => {
  const labels = {
    FACEBOOK: "Facebook",
    INSTAGRAM: "Instagram",
    PINTEREST: "Pinterest",
    LINKEDIN_COMPANY: "LinkedIn Business",
    YOUTUBE: "YouTube",
  };

  return labels[platform] || platform;
};

const formatNumber = (value) => {
  const numericValue = Number(value || 0);
  return numericValue.toLocaleString();
};

const getDateRangeFromPreset = (preset) => {
  const today = new Date();
  const end = new Date(today);
  end.setHours(23, 59, 59, 999);

  const start = new Date(today);

  if (preset === "7") {
    start.setDate(start.getDate() - 6);
  } else if (preset === "14") {
    start.setDate(start.getDate() - 13);
  } else if (preset === "28") {
    start.setDate(start.getDate() - 27);
  } else if (preset === "45") {
    start.setDate(start.getDate() - 44);
  } else if (preset === "60") {
    start.setDate(start.getDate() - 59);
  } else if (preset === "90") {
    start.setDate(start.getDate() - 89);
  } else {
    return { since: null, until: null };
  }

  start.setHours(0, 0, 0, 0);

  return {
    since: start.toISOString(),
    until: end.toISOString(),
  };
};

export default function PostReports() {
  const { groups, selectedGroup, profile: availableProfiles } = useSelector(
    (state) => state.groups
  );

  const { subscription } = useSelector((state) => state.subscription);

  const hasSubscriptionPlan = Boolean(subscription?.plan);
  const reportsEnabled = subscription?.plan?.reports_enabled === true;

  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [datePreset, setDatePreset] = useState("28");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const [upgradeModal, setUpgradeModal] = useState({
    visible: false,
    message: "",
    featureName: "Reports",
  });

  const selectedGroupObj = useMemo(
    () => groups.find((g) => g.name === selectedGroup),
    [groups, selectedGroup]
  );

  const platformOptions = useMemo(() => {
    const uniquePlatforms = [
      ...new Set((availableProfiles || []).map((p) => p.platform)),
    ];

    return uniquePlatforms.filter((platform) =>
      SUPPORTED_REPORT_PLATFORMS.includes(platform)
    );
  }, [availableProfiles]);

  useEffect(() => {
    if (platformOptions.length === 0) {
      setSelectedPlatform("");
      return;
    }

    if (!selectedPlatform || !platformOptions.includes(selectedPlatform)) {
      setSelectedPlatform(platformOptions[0]);
    }
  }, [platformOptions, selectedPlatform]);

  const filteredProfiles = useMemo(() => {
    return (availableProfiles || []).filter(
      (p) => p.platform === selectedPlatform
    );
  }, [availableProfiles, selectedPlatform]);

  useEffect(() => {
    if (filteredProfiles.length > 0) {
      const profileIds = filteredProfiles.map(
        (profile) => profile.platform_pageId || profile.platform_id
      );

      if (!selectedProfileId || !profileIds.includes(selectedProfileId)) {
        setSelectedProfileId(profileIds[0]);
      }
    } else {
      setSelectedProfileId(null);
    }

    setPage(1);
  }, [filteredProfiles, selectedProfileId]);

  const openReportsUpgradeModal = (message = REPORTS_UPGRADE_MESSAGE) => {
    setUpgradeModal({
      visible: true,
      featureName: "Reports",
      message,
    });
  };

  const resetTableState = () => {
    setItems([]);
    setPagination({
      page: 1,
      limit: 20,
      total: 0,
      pages: 0,
    });
  };

  const isPinterest = selectedPlatform === "PINTEREST";
  const isLinkedInCompany = selectedPlatform === "LINKEDIN_COMPANY";
  const isYouTube = selectedPlatform === "YOUTUBE";
  const isInstagram = selectedPlatform === "INSTAGRAM";

  useEffect(() => {
    const loadReports = async () => {
      if (hasSubscriptionPlan && !reportsEnabled) {
        resetTableState();
        openReportsUpgradeModal();
        return;
      }

      if (!selectedGroupObj?._id || !selectedProfileId || !selectedPlatform) {
        resetTableState();
        return;
      }

      setLoading(true);

      try {
        const { since, until } = getDateRangeFromPreset(datePreset);

        const response = await fetchPostReports({
          groupId: selectedGroupObj._id,
          platform: selectedPlatform,
          profileId: selectedProfileId,
          page,
          limit: 20,
          ...(since && { since }),
          ...(until && { until }),
        });

        setItems(response.items || []);
        setPagination(
          response.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            pages: 0,
          }
        );
      } catch (error) {
        console.error(error);
        resetTableState();

        if (error.isLimitError || error.errorType === "LIMIT_REACHED") {
          openReportsUpgradeModal(error.message || REPORTS_UPGRADE_MESSAGE);
        }
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [
    selectedGroupObj,
    selectedProfileId,
    selectedPlatform,
    datePreset,
    page,
    hasSubscriptionPlan,
    reportsEnabled,
  ]);

  const columns = [
    {
      title: "Preview",
      key: "preview",
      width: 90,
      render: (_, record) => {
        const previewImage =
          record.preview?.image_url?.[0] ||
          record.preview?.source_image_url?.[0] ||
          null;

        const previewVideo =
          record.preview?.video_url?.[0] ||
          record.preview?.source_video_url?.[0] ||
          null;

        if (previewImage) {
          return (
            <img
              src={previewImage}
              alt="preview"
              className="w-14 h-14 object-cover rounded-md border"
            />
          );
        }

        if (previewVideo) {
          return (
            <video
              src={previewVideo}
              className="w-14 h-14 object-cover rounded-md border bg-black"
              muted
            />
          );
        }

        return (
          <div className="w-14 h-14 bg-gray-100 rounded-md border flex items-center justify-center text-xs text-gray-400">
            -
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "postDate",
      key: "postDate",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: 260,
      render: (value) => (
        <div className="max-w-[240px] truncate" title={value}>
          {value || "-"}
        </div>
      ),
    },
    {
      title: "Post Type",
      dataIndex: "post_type",
      key: "post_type",
      render: (value) => value || "-",
    },
    {
      title: "Total Reactions",
      dataIndex: ["metrics", "total_reactions"],
      key: "total_reactions",
      render: (value) => formatNumber(value),
      hidden: isPinterest || isYouTube || isLinkedInCompany,
    },
    {
      title: "Likes",
      dataIndex: ["metrics", "likes"],
      key: "likes",
      render: (value) => formatNumber(value),
      hidden: isPinterest,
    },
    {
      title: "Comments",
      dataIndex: ["metrics", "comments"],
      key: "comments",
      render: (value) => formatNumber(value),
      hidden: isPinterest,
    },
    {
      title: "Shares",
      dataIndex: ["metrics", "shares"],
      key: "shares",
      render: (value) => formatNumber(value),
      hidden: isPinterest,
    },
    {
      title: "Impressions",
      dataIndex: ["metrics", "impressions"],
      key: "impressions",
      render: (value) => formatNumber(value),
      hidden: isYouTube,
    },
    {
      title: "Reach",
      dataIndex: ["metrics", "reach"],
      key: "reach",
      render: (value) => formatNumber(value),
      hidden: isPinterest || isYouTube,
    },
    {
      title: "Clicks",
      dataIndex: ["metrics", "clicks"],
      key: "clicks",
      render: (value) => formatNumber(value),
      hidden: isYouTube || isPinterest,
    },
    {
      title: "Engagements",
      dataIndex: ["metrics", "engagements"],
      key: "engagements",
      render: (value) => formatNumber(value),
    },
    {
      title: "Saved",
      key: "saved",
      render: (_, record) =>
        isInstagram
          ? record.raw?.instagram_media_insights?.find?.(
              (m) => m.name === "saved"
            )?.values?.[0]?.value ?? "-"
          : isPinterest
          ? record.reaction_breakdown?.SAVE ?? 0
          : "-",
      hidden: !isInstagram && !isPinterest,
    },
    {
      title: "Pin Clicks",
      key: "pin_clicks",
      render: (_, record) =>
        isPinterest ? record.reaction_breakdown?.PIN_CLICK ?? 0 : "-",
      hidden: !isPinterest,
    },
    {
      title: "Outbound Clicks",
      key: "outbound_clicks",
      render: (_, record) =>
        isPinterest ? record.reaction_breakdown?.OUTBOUND_CLICK ?? 0 : "-",
      hidden: !isPinterest,
    },
    {
      title: "Engagement Rate %",
      dataIndex: ["metrics", "engagement_rate_per_impression"],
      key: "engagement_rate_per_impression",
      render: (value) => Number(value || 0),
    },
    {
      title: "Video Views",
      dataIndex: ["metrics", "video_views"],
      key: "video_views",
      render: (value) => formatNumber(value),
      hidden: !isYouTube && !isPinterest,
    },
    {
      title: "Watch Time (min)",
      dataIndex: ["metrics", "video_view_time"],
      key: "video_view_time",
      render: (value) => formatNumber(value),
      hidden: !isYouTube,
    },
    {
      title: "Link",
      dataIndex: "platform_url",
      key: "platform_url",
      render: (value) =>
        value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Open
          </a>
        ) : (
          "-"
        ),
    },
  ].filter((col) => !col.hidden);

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Post Reports</h1>
          <p className="text-gray-500">Post-level metrics from stored reports.</p>
        </div>

        <Card className="rounded-xl shadow-sm mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[220px]">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Platform
              </label>
              <Select
                value={selectedPlatform || undefined}
                onChange={(value) => {
                  setSelectedPlatform(value);
                  setSelectedProfileId(null);
                  setPage(1);
                }}
                style={{ width: "100%" }}
                placeholder="Select Platform"
              >
                {platformOptions.map((platform) => (
                  <Option key={platform} value={platform}>
                    {getPlatformLabel(platform)}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="min-w-[260px]">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Profile
              </label>
              <Select
                value={selectedProfileId || undefined}
                onChange={(value) => {
                  setSelectedProfileId(value);
                  setPage(1);
                }}
                style={{ width: "100%" }}
                placeholder="Select Profile"
              >
                {filteredProfiles.map((p, idx) => (
                  <Option
                    key={`${p.platform}-${p.platform_pageId || p.platform_id}-${idx}`}
                    value={p.platform_pageId || p.platform_id}
                  >
                    {p.profile || p.username || p.platform_id || p.platform_pageId}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Date Range
              </label>
              <Select
                value={datePreset}
                onChange={(value) => {
                  setDatePreset(value);
                  setPage(1);
                }}
                style={{ width: "100%" }}
              >
                <Option value="7">Last 7 days</Option>
                <Option value="14">Last 14 days</Option>
                <Option value="28">Last 28 days</Option>
                <Option value="45">Last 45 days</Option>
                <Option value="60">Last 60 days</Option>
                <Option value="90">Last 90 days</Option>
                <Option value="max">All time</Option>
              </Select>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border shadow-sm p-10">
            <Empty description="No post report data found yet." />
          </div>
        ) : (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <Table
              rowKey={(record) => record._id}
              columns={columns}
              dataSource={items}
              pagination={false}
              scroll={{ x: 1100 }}
            />

            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="text-sm text-gray-500">
                Page {pagination.page} of {pagination.pages || 1} ·{" "}
                {pagination.total} total
              </div>

              <div className="flex gap-2">
                <Button
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  Previous
                </Button>

                <Button
                  disabled={page >= (pagination.pages || 1)}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <UpgradeModal
        isOpen={upgradeModal.visible}
        onClose={() =>
          setUpgradeModal({
            visible: false,
            message: "",
            featureName: "Reports",
          })
        }
        message={upgradeModal.message}
        featureName={upgradeModal.featureName}
      />
    </>
  );
}